<script setup lang="ts">
import { DEFAULT_SECTION_TITLES, type TextSettings, type ThemeSettings, type SectionSettings } from '~/stores/auth'
import type { FriendDisplayItem } from '~/composables/useCharacterPage'

const props = defineProps<{
  friends: FriendDisplayItem[]
  textSettings: TextSettings
  themeSettings: ThemeSettings
  sectionSettings: SectionSettings
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-friends-manager': []
}>()

// Heading and divider styles
const injectedResolvedFont = inject<ComputedRef<string>>('resolvedFontFamily', computed(() => ''))
const headingStyle = computed(() => ({
  fontSize: `calc(1rem * ${props.textSettings.fontScale})`,
  fontWeight: props.textSettings.fontWeight,
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 70%, transparent)`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em'
}))

const injectedSeparatorStyle = inject<Ref<Record<string, string> | null>>('separatorStyle', ref({ background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }))
const dividerStyle = computed(() => injectedSeparatorStyle.value ?? { background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' })

const inCard = inject<Ref<boolean>>('sectionInCard', ref(false))
const showTitles = computed(() => props.sectionSettings.showTitles !== false)

// Custom section titles
const authStore = useAuthStore()
const { requirePlan } = usePlanGate()
const sectionTitlesEnabled = computed(() => authStore.canAccessBoolean('customSectionTitles'))
const sectionTitle = computed(() => props.sectionSettings.titles?.friends || DEFAULT_SECTION_TITLES.friends)
const updateSectionTitle = inject<(key: string, value: string) => void>('updateSectionTitle', () => {})

const nameStyle = computed(() => ({
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 60%, transparent)`
}))

// Friend links must never be relative on a custom-domain host: /friendname
// would resolve on the owner's domain and redirect back to the owner's page.
// A friend's own domain wins; otherwise link to their mychar.gg page.
const { isCustomDomain } = useCustomDomain()
const siteUrl = useRuntimeConfig().public.siteUrl

function friendLink(friend: FriendDisplayItem): string {
  if (friend.custom_domain) return `https://${friend.custom_domain}`
  if (isCustomDomain.value) return `${siteUrl}/${friend.username}`
  return `/${friend.username}`
}
</script>

<template>
  <div
    v-if="friends.length > 0 || editMode"
    class="px-2"
    :class="{ 'mt-6': showTitles || !inCard }"
  >
    <!-- Section heading with divider -->
    <div
      v-if="showTitles || !inCard"
      class="flex items-center gap-3 mb-4"
    >
      <div
        class="flex-1 h-px"
        :style="dividerStyle"
      />
      <InlineEditText
        v-if="showTitles && editMode && sectionTitlesEnabled"
        :model-value="sectionSettings.titles?.friends ?? ''"
        :editable="true"
        tag="h3"
        :placeholder="DEFAULT_SECTION_TITLES.friends"
        :display-style="headingStyle"
        class="shrink-0"
        @update:model-value="updateSectionTitle('friends', $event)"
      />
      <div
        v-else-if="showTitles && editMode"
        class="shrink-0 inline-flex items-center gap-1.5 cursor-pointer rounded-lg transition-colors hover:bg-white/5 px-2 py-0.5"
        @click="requirePlan('Custom Section Titles', 'pro')"
      >
        <h3
          class="text-xs"
          :style="headingStyle"
        >
          {{ DEFAULT_SECTION_TITLES.friends }}
        </h3>
        <UBadge
          color="fuchsia"
          variant="solid"
          size="xs"
        >
          <UIcon
            name="i-lucide-pencil"
            class="size-2.5"
          />
        </UBadge>
      </div>
      <template v-else-if="showTitles">
        <h3
          class="text-xs shrink-0"
          :style="headingStyle"
        >
          {{ sectionTitle }}
        </h3>
        <span
          class="text-xs opacity-40"
          :style="{ fontFamily: headingStyle.fontFamily }"
        >
          {{ friends.length }}
        </span>
      </template>
      <div
        v-if="showTitles"
        class="flex-1 h-px"
        :style="dividerStyle"
      />
    </div>

    <!-- Friends grid -->
    <div
      v-if="friends.length > 0"
      class="flex flex-wrap justify-center gap-4"
    >
      <NuxtLink
        v-for="friend in friends"
        :key="friend.character_id"
        :to="friendLink(friend)"
        target="_blank"
        class="group flex flex-col items-center gap-1.5 transition-transform duration-200 hover:scale-105"
      >
        <div class="size-10 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/10 group-hover:ring-white/25 transition-all duration-200">
          <img
            v-if="friend.avatar_url"
            :src="friend.avatar_url"
            :alt="friend.username"
            class="size-full object-cover"
          >
          <div
            v-else
            class="size-full flex items-center justify-center text-white/20"
          >
            <UIcon
              name="i-lucide-user"
              class="size-5"
            />
          </div>
        </div>
        <span
          class="text-[10px] leading-tight max-w-[56px] truncate"
          :style="nameStyle"
        >
          {{ friend.username }}
        </span>
      </NuxtLink>
    </div>

    <!-- Manage button (edit mode) -->
    <button
      v-if="editMode"
      class="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-dashed transition-all duration-200 hover:scale-105 hover:border-white/15"
      :style="{
        borderColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.4)'
      }"
      @click="emit('open-friends-manager')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage friends</span>
    </button>
  </div>
</template>
